import { useState, useEffect, useCallback, useRef } from 'react';
import { appliquerModele } from '../utils/modelesOuvertures';
import { validerDimensionsMur, validerOuverture } from '../utils/validators';

// Vecteur de déplacement induit par un mur (le long de sa face) selon sa rotation.
// rotation peut être un nombre (angle Y) ou un tableau [x,y,z] (plafond).
const deplacementMur = (rotation, largeur) => {
  const theta = Array.isArray(rotation) ? (rotation[1] || 0) : (rotation || 0);
  return {
    dx: largeur * Math.cos(theta),
    dz: -largeur * Math.sin(theta)
  };
};

// Rechaîne les positions des murs pour qu'ils restent connectés bout à bout
// après un changement de dimension. Le premier mur du périmètre reste ancré ;
// chaque mur place le suivant. Le plafond suit l'emprise au sol.
const recalculerPositionsMurs = (listeMurs) => {
  if (!Array.isArray(listeMurs) || listeMurs.length === 0) return listeMurs;

  const perimetre = listeMurs.filter(m => m.type !== 'plafond');
  const hauteurPiece = perimetre[0]?.hauteur || listeMurs[0]?.hauteur || 0;
  const empriseLargeur = perimetre[0]?.largeur;
  const empriseProfondeur = perimetre[1]?.largeur;

  let precedent = null;

  return listeMurs.map(m => {
    if (m.type === 'plafond') {
      return {
        ...m,
        position: { x: 0, y: hauteurPiece, z: 0 },
        largeur: empriseLargeur ?? m.largeur,
        hauteur: empriseProfondeur ?? m.hauteur
      };
    }

    let position;
    if (!precedent) {
      position = m.position || { x: 0, y: 0, z: 0 };
    } else {
      const { dx, dz } = deplacementMur(precedent.rotation, precedent.largeur);
      position = {
        x: (precedent.position?.x || 0) + dx,
        y: precedent.position?.y || 0,
        z: (precedent.position?.z || 0) + dz
      };
    }

    const nouveau = { ...m, position };
    precedent = nouveau;
    return nouveau;
  });
};

const useProjetStore = () => {
  // État pour les dimensions des plaques disponibles
  const [plaqueDimensions, setPlaqueDimensions] = useState({
    largeur: 120, // cm
    hauteur: 250  // cm
  });

  // État pour les murs du projet
  const [murs, setMurs] = useState([
    {
      id: 1,
      nom: "Mur 1",
      largeur: 300,
      hauteur: 250,
      ouvertures: [],
      position: { x: 0, y: 0, z: 0 },
      rotation: 0
    }
  ]);

  // Historique pour undo/redo
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const maxHistory = 30;
  const isUndoing = useRef(false);

  // Sauvegarder l'état dans l'historique
  const saveToHistory = useCallback((mursState) => {
    if (isUndoing.current) return;
    
    setHistory(prev => {
      // Supprimer les états futurs si on est au milieu de l'historique
      const newHistory = prev.slice(0, historyIndex + 1);
      // Ajouter le nouvel état
      newHistory.push(JSON.parse(JSON.stringify(mursState)));
      // Limiter la taille
      if (newHistory.length > maxHistory) {
        newHistory.shift();
        setHistoryIndex(maxHistory - 1);
      } else {
        setHistoryIndex(newHistory.length - 1);
      }
      return newHistory;
    });
  }, [historyIndex]);

  // Undo
  const undo = useCallback(() => {
    if (historyIndex > 0) {
      isUndoing.current = true;
      const newIndex = historyIndex - 1;
      setHistoryIndex(newIndex);
      setMurs(JSON.parse(JSON.stringify(history[newIndex])));
      setTimeout(() => { isUndoing.current = false; }, 0);
    }
  }, [history, historyIndex]);

  // Redo
  const redo = useCallback(() => {
    if (historyIndex < history.length - 1) {
      isUndoing.current = true;
      const newIndex = historyIndex + 1;
      setHistoryIndex(newIndex);
      setMurs(JSON.parse(JSON.stringify(history[newIndex])));
      setTimeout(() => { isUndoing.current = false; }, 0);
    }
  }, [history, historyIndex]);

  // Initialiser l'historique
  useEffect(() => {
    if (history.length === 0) {
      setHistory([JSON.parse(JSON.stringify(murs))]);
      setHistoryIndex(0);
    }
  }, []);

  // État pour les erreurs
  const [erreurs, setErreurs] = useState({});

  // État pour le mur actuellement sélectionné
  const [murSelectionneeId, setMurSelectionneeId] = useState(1);

  // Obtenir le mur sélectionné
  const murSelectionnee = murs.find(m => m.id === murSelectionneeId) || murs[0];

  // État pour l'ouverture actuellement sélectionnée
  const [ouvertureSelectionneeId, setOuvertureSelectionneeId] = useState(null);

  // Pour ajouter un nouveau mur
  const ajouterMur = () => {
    let position = { x: 0, y: 0, z: 0 };
    let rotation = 0;

    if (murs.length > 0) {
      const lastWall = murs[murs.length - 1];
      if (lastWall.position) {
        const lastW = lastWall.largeur;
        // Gérer le cas où rotation est un tableau ou un nombre
        const lastRot = Array.isArray(lastWall.rotation) ? lastWall.rotation[1] : (lastWall.rotation || 0);

        position = {
          x: lastWall.position.x + lastW * Math.cos(lastRot),
          y: lastWall.position.y || 0,
          z: lastWall.position.z - lastW * Math.sin(lastRot)
        };

        // Tourner de 90 degrés vers la droite par défaut
        rotation = lastRot - Math.PI / 2;
      } else {
        // Fallback si le dernier mur n'a pas de position
        position = { x: (murs.length) * 300, y: 0, z: 0 };
      }
    }

    const nouveauMur = {
      id: Date.now(),
      nom: `Mur ${murs.length + 1}`,
      largeur: 300,
      hauteur: 250,
      ouvertures: [],
      position,
      rotation
    };
    const newMurs = [...murs, nouveauMur];
    setMurs(newMurs);
    saveToHistory(newMurs);
    setMurSelectionneeId(nouveauMur.id);
  };

  // Pour supprimer un mur
  const supprimerMur = (id) => {
    if (murs.length <= 1) {
      alert("Impossible de supprimer le dernier mur");
      return;
    }
    const nouveauxMurs = murs.filter(m => m.id !== id);
    setMurs(nouveauxMurs);
    saveToHistory(nouveauxMurs);
    if (murSelectionneeId === id) {
      setMurSelectionneeId(nouveauxMurs[0].id);
    }
  };

  // Committer l'état courant dans l'historique (fin d'une interaction en
  // temps réel : relâchement d'un drag, sortie d'un champ de dimension).
  const commitToHistory = () => {
    saveToHistory(murs);
  };

  // Pour modifier un mur
  const modifierMur = (champ, valeur) => {
    if (champ === 'largeur' || champ === 'hauteur') {
      const erreur = validerDimensionsMur(murSelectionnee, champ, valeur);

      if (erreur) {
        setErreurs({ ...erreurs, [murSelectionneeId]: { ...(erreurs[murSelectionneeId] || {}), [champ]: erreur } });
        return;
      } else {
        // Effacer l'erreur si elle existe
        if (erreurs[murSelectionneeId] && erreurs[murSelectionneeId][champ]) {
          const nouvellesErreurs = { ...erreurs };
          delete nouvellesErreurs[murSelectionneeId][champ];
          if (Object.keys(nouvellesErreurs[murSelectionneeId]).length === 0) {
            delete nouvellesErreurs[murSelectionneeId];
          }
          setErreurs(nouvellesErreurs);
        }
      }
    }

    let newMurs = murs.map(m => {
      if (m.id === murSelectionneeId) {
        return { ...m, [champ]: valeur };
      }
      return m;
    });

    // Un changement de dimension peut décaler les raccords : on rechaîne les
    // positions pour que la pièce reste fermée (murs connectés bout à bout).
    if (champ === 'largeur' || champ === 'hauteur') {
      newMurs = recalculerPositionsMurs(newMurs);
    }

    setMurs(newMurs);
    // Ne pas sauvegarder pour les changements de dimensions en temps réel
    // sauf si c'est un changement de nom
    if (champ === 'nom') {
      saveToHistory(newMurs);
    }
  };

  // Créer l'ouverture avec le type choisi
  const ajouterOuverture = (type, position) => {
    const baseOuverture = {
      id: Date.now(),
      x: position.x || 30,
      y: position.y || 30
    };

    const nouvelleOuverture = appliquerModele(type, baseOuverture);

    const newMurs = murs.map(m => {
      if (m.id === murSelectionneeId) {
        return {
          ...m,
          ouvertures: [...m.ouvertures, nouvelleOuverture]
        };
      }
      return m;
    });
    setMurs(newMurs);
    saveToHistory(newMurs);

    setOuvertureSelectionneeId(nouvelleOuverture.id);
    return nouvelleOuverture;
  };

  // Pour supprimer une ouverture du mur sélectionné
  const supprimerOuverture = (id) => {
    const newMurs = murs.map(m => {
      if (m.id === murSelectionneeId) {
        return {
          ...m,
          ouvertures: m.ouvertures.filter(o => o.id !== id)
        };
      }
      return m;
    });
    setMurs(newMurs);
    saveToHistory(newMurs);

    if (ouvertureSelectionneeId === id) {
      setOuvertureSelectionneeId(null);
    }
  };

  // Mettre à jour les dimensions de l'ouverture sélectionnée
  const modifierOuverture = (id, champ, valeur) => {
    const ouverture = murSelectionnee.ouvertures.find(o => o.id === id);
    if (!ouverture) return;

    // Valider la modification
    const erreur = validerOuverture(murSelectionnee, ouverture, champ, valeur);

    if (erreur) {
      // Stocker l'erreur
      setErreurs({
        ...erreurs,
        [murSelectionneeId]: {
          ...(erreurs[murSelectionneeId] || {}),
          [id]: {
            ...(erreurs[murSelectionneeId]?.[id] || {}),
            [champ]: erreur
          }
        }
      });
      return;
    } else {
      // Effacer l'erreur si elle existe
      if (erreurs[murSelectionneeId]?.[id]?.[champ]) {
        const nouvellesErreurs = { ...erreurs };
        delete nouvellesErreurs[murSelectionneeId][id][champ];

        if (Object.keys(nouvellesErreurs[murSelectionneeId][id]).length === 0) {
          delete nouvellesErreurs[murSelectionneeId][id];
        }

        if (Object.keys(nouvellesErreurs[murSelectionneeId]).length === 0) {
          delete nouvellesErreurs[murSelectionneeId];
        }

        setErreurs(nouvellesErreurs);
      }
    }

    const newMurs = murs.map(m => {
      if (m.id === murSelectionneeId) {
        return {
          ...m,
          ouvertures: m.ouvertures.map(o => {
            if (o.id === id) {
              return { ...o, [champ]: valeur };
            }
            return o;
          })
        };
      }
      return m;
    });
    setMurs(newMurs);
    // Sauvegarder seulement pour certains changements (pas le drag & drop en temps réel)
    if (champ !== 'x' && champ !== 'y') {
      saveToHistory(newMurs);
    }
  };

  // Exposer les fonctions pour l'export PDF et le débogage
  useEffect(() => {
    window.onModifierOuverture = modifierOuverture;
    window.onSelectMur = setMurSelectionneeId;
    return () => {
      window.onModifierOuverture = undefined;
      window.onSelectMur = undefined;
    };
  }, [murs, murSelectionneeId, modifierOuverture]);

  // Créer une pièce rectangulaire (ensemble de 4 murs + éventuel plafond)
  const creerPieceRectangulaire = (longueur, largeur, hauteur, avecPlafond = false) => {
    const now = Date.now();

    // Mur 1 (Fond - Nord) : Longueur = largeur, Départ (0,0)
    const mur1 = {
      id: now,
      nom: "Mur 1",
      largeur: largeur,
      hauteur: hauteur,
      ouvertures: [],
      position: { x: 0, y: 0, z: 0 },
      rotation: 0 // 0 rad
    };

    // Mur 2 (Droite - Est) : Longueur = longueur, Départ (largeur, 0)
    const mur2 = {
      id: now + 1,
      nom: "Mur 2",
      largeur: longueur,
      hauteur: hauteur,
      ouvertures: [],
      position: { x: largeur, y: 0, z: 0 },
      rotation: -Math.PI / 2 // -90 deg
    };

    // Mur 3 (Devant - Sud) : Longueur = largeur, Départ (largeur, longueur)
    const mur3 = {
      id: now + 2,
      nom: "Mur 3",
      largeur: largeur,
      hauteur: hauteur,
      ouvertures: [],
      position: { x: largeur, y: 0, z: longueur },
      rotation: -Math.PI // -180 deg
    };

    // Mur 4 (Gauche - Ouest) : Longueur = longueur, Départ (0, longueur)
    const mur4 = {
      id: now + 3,
      nom: "Mur 4",
      largeur: longueur,
      hauteur: hauteur,
      ouvertures: [],
      position: { x: 0, y: 0, z: longueur },
      rotation: -3 * Math.PI / 2 // -270 deg (ou 90 deg)
    };

    // Liste des murs
    const nouveauxMurs = [mur1, mur2, mur3, mur4];

    // Plafond (Optionnel)
    if (avecPlafond) {
      const plafond = {
        id: now + 4,
        nom: "Plafond",
        type: 'plafond', // Identification explicite
        // Dimensions du plafond correspondent à la surface au sol
        largeur: largeur,  // Comme Mur 1
        hauteur: longueur, // Profondeur de la pièce
        ouvertures: [],
        position: { x: 0, y: hauteur, z: 0 }, // Posé en haut des murs
        // Rotation pour le mettre à plat, dans le sens des z positifs
        // (là où se trouvent les murs) : +90° autour de X.
        rotation: [Math.PI / 2, 0, 0]
      };
      nouveauxMurs.push(plafond);
    }

    setMurs(nouveauxMurs);
    saveToHistory(nouveauxMurs);
    setMurSelectionneeId(mur1.id);
  };

  // Charger un projet sauvegardé
  const loadProject = (loadedMurs, loadedPlaqueDimensions) => {
    if (loadedPlaqueDimensions) {
      setPlaqueDimensions(loadedPlaqueDimensions);
    }
    if (loadedMurs && loadedMurs.length > 0) {
      setMurs(loadedMurs);
      saveToHistory(loadedMurs);
      setMurSelectionneeId(loadedMurs[0].id);
    }
    setErreurs({});
    setOuvertureSelectionneeId(null);
  };

  // Créer une configuration en L (2 murs)
  const creerPieceEnL = (longueur, largeur, hauteur, avecPlafond = false) => {
    const now = Date.now();

    // Mur 1 (Principal)
    const mur1 = {
      id: now,
      nom: "Mur 1",
      largeur: largeur,
      hauteur: hauteur,
      ouvertures: [],
      position: { x: 0, y: 0, z: 0 },
      rotation: 0
    };

    // Mur 2 (Retour)
    const mur2 = {
      id: now + 1,
      nom: "Mur 2",
      largeur: longueur,
      hauteur: hauteur,
      ouvertures: [],
      position: { x: largeur, y: 0, z: 0 },
      rotation: -Math.PI / 2
    };

    const nouveauxMurs = [mur1, mur2];

    setMurs(nouveauxMurs);
    saveToHistory(nouveauxMurs);
    setMurSelectionneeId(mur1.id);
  };

  return {
    state: {
      murs,
      murSelectionneeId,
      murSelectionnee,
      ouvertureSelectionneeId,
      plaqueDimensions,
      erreurs
    },
    actions: {
      setMurs,
      setMurSelectionneeId,
      setPlaqueDimensions,
      setOuvertureSelectionneeId,
      ajouterMur,
      supprimerMur,
      modifierMur,
      ajouterOuverture,
      supprimerOuverture,
      modifierOuverture,
      commitToHistory,
      creerPieceRectangulaire,
      creerPieceEnL,
      loadProject,
      undo,
      redo
    },
    loadProject,
    canUndo: historyIndex > 0,
    canRedo: historyIndex < history.length - 1
  };
};

export default useProjetStore;
